<template>
  <teleport v-if="isModalActive" to="body">
    <div class="modal" :class="{ 'is-card': card }">
      <div class="modal-background" @click.prevent="closeModal"></div>
      <div class="modal-content">
        <button
          v-if="card"
          class="modal-close-inside"
          aria-label="close"
          @click="closeModal"
        ></button>
        <div class="box">
          <div class="content">
            <h3 v-if="$slots.title" class="title">
              <slot name="title"></slot>
            </h3>
            <div class="body">
              <slot></slot>
            </div>
          </div>
        </div>
      </div>
      <button
        v-if="!card"
        class="modal-close"
        aria-label="close"
        @click="closeModal"
      ></button>
    </div>
  </teleport>
</template>

<script setup>
import { watch, onBeforeUnmount } from "vue";

const props = defineProps({
  isModalActive: {
    type: Boolean,
    default: false,
  },
  // card: крестик внутри карточки + компактная карточка на мобиле (не full-screen)
  card: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close"]);

const closeModal = () => {
  emit("close");
};

const handleKeyDown = (event) => {
  if (event.key === "Escape") {
    closeModal();
  }
};

watch(
  () => props.isModalActive,
  (newVal) => {
    if (newVal) {
      window.addEventListener("keydown", handleKeyDown);
    } else {
      window.removeEventListener("keydown", handleKeyDown);
    }
  },
);

onBeforeUnmount(() => {
  window.removeEventListener("keydown", handleKeyDown);
});
</script>

<style lang="scss" scoped>
.modal,
.modal-background {
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
}

.modal {
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  position: fixed;
  z-index: 1000;
}

.modal-background {
  background-color: hsla(221, 14%, 4%, 0.86);
}

.modal-close {
  z-index: 100000;
  position: fixed;
  top: 20px;
  right: 20px;
  height: 50px;
  width: 50px;
  max-width: 50px;
  max-height: 50px;

  &:after {
    height: 50%;
    width: 2px;
  }

  &:before {
    height: 2px;
    width: 50%;
  }

  &:after,
  &:before {
    background-color: white;
    content: "";
    display: block;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translateX(-50%) translateY(-50%) rotate(45deg);
    transform-origin: center center;
  }
}

.modal-close-inside {
  position: absolute;
  top: 18px;
  right: 18px;
  height: 32px;
  width: 32px;
  z-index: 10;
  cursor: pointer;
  background: transparent;
  border: none;

  &:before,
  &:after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 18px;
    height: 2px;
    background-color: var(--font-color);
    opacity: 0.55;
  }
  &:before {
    transform: translate(-50%, -50%) rotate(45deg);
  }
  &:after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }
  &:hover:before,
  &:hover:after {
    opacity: 1;
  }
}

// card-режим: уже карточка, текст заполняет её (не зажат в 360px), компактнее отступы
.modal.is-card .modal-content {
  max-width: 440px;

  .box {
    padding: 44px 30px 34px;
  }

  .content {
    max-width: 100%;
  }
}

.modal-content {
  position: relative;
  border-radius: 26px;
  width: 100%;
  max-width: 560px;
  border: none;
  background-color: var(--body-color);
  z-index: 1;
  overflow-y: auto;
  overflow-x: hidden;

  .box {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 70px 30px;
    background-color: var(--body-color);

    .content {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      max-width: 360px;

      .title {
        text-align: center;
        font-size: 32px;
        font-weight: 700;
        line-height: 38.4px;
        color: var(--font-color);
        margin-bottom: 25px;
      }

      .body {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 18px;
        width: 100%;
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .modal-content {
    max-width: 450px;

    .box {
      padding: 30px;
    }
  }
}

@media screen and (max-width: 568px) {
  .modal-background {
    background-color: var(--body-color);
  }

  .modal-content {
    max-width: 95vw;
    max-height: calc(100vh - 80px);
    border: none;

    .box {
      padding-top: 0;
      padding-bottom: 0;

      .content {
        .title {
          font-size: 28px;
        }
      }
    }
  }
  .modal-close {
    top: 5px;
    right: 5px;
  }

  .modal-close::before,
  .modal-close::after {
    background-color: var(--font-color);
  }

  // card-режим: остаётся компактной карточкой по центру, фон тёмный, крестик внутри
  .modal.is-card .modal-background {
    background-color: hsla(221, 14%, 4%, 0.86);
  }
  .modal.is-card .modal-content {
    max-width: min(90vw, 400px);
    max-height: calc(100vh - 40px);

    .box {
      padding: 48px 24px 32px;
    }
  }
  .modal.is-card .modal-close-inside {
    top: 14px;
    right: 14px;
  }
}
</style>
